import { CheckCircleSolid, Pet } from '@awazevr/design-system.components.icon'
import { Stack } from '@awazevr/design-system.components.stack'
import { Text } from '@awazevr/design-system.components.text'
import {
  StyledKSPItem,
  StyledMobileKSPs,
  StyledTabletKSPs,
} from './KSPs.styles'

export type KSPsProps = {
  variant: 'tablet' | 'mobile'
  includedExtras?: string[] | null
  keySellingPoints?: string[] | null
  hasPetsFree?: boolean
  petsFreeLabel?: string
  code?: string
}

type Item = { key: string; label: string; icon: 'check' | 'pet' }

const buildIncludedExtras = (
  extras: string[] | null | undefined,
  code: string | undefined,
): Item[] =>
  (extras || []).map((extra) => ({
    key: `property-included-${extra}-${code}`,
    label: extra,
    icon: 'check',
  }))

const buildPetsFreeItem = (
  hasPetsFree: boolean,
  petsFreeLabel: string | undefined,
  code: string | undefined,
): Item | null =>
  hasPetsFree && petsFreeLabel
    ? {
        key: `${code}-property-ksp-pets-free`,
        label: petsFreeLabel,
        icon: 'pet',
      }
    : null

const buildKeySellingPoints = (
  ksps: string[] | null | undefined,
  code: string | undefined,
): Item[] =>
  (ksps || []).map((ksp) => ({
    key: `${code}-property-ksp-${ksp}`,
    label: ksp,
    icon: 'check',
  }))

const renderItem = (item: Item) => (
  <StyledKSPItem key={item.key}>
    {item.icon === 'pet' ? (
      <Pet iconColour="ColorBrandSecondary" size="small" />
    ) : (
      <CheckCircleSolid iconColour="ColorStatusConstructive300" size="small" />
    )}
    <Text sizes={200} type="regular">
      {item.label}
    </Text>
  </StyledKSPItem>
)

export const KSPs = ({
  variant,
  includedExtras,
  keySellingPoints,
  hasPetsFree = false,
  petsFreeLabel,
  code,
}: KSPsProps) => {
  const hasIncludedExtras = !!includedExtras && includedExtras.length > 0
  const hasKeySellingPoints = !!keySellingPoints && keySellingPoints.length > 0

  if (!hasIncludedExtras && !hasKeySellingPoints) return null

  const includedExtrasItems = buildIncludedExtras(includedExtras, code)
  const petsFreeItem = buildPetsFreeItem(hasPetsFree, petsFreeLabel, code)
  const keySellingPointItems = buildKeySellingPoints(keySellingPoints, code)

  if (variant === 'tablet') {
    return (
      <>
        {hasIncludedExtras && (
          <StyledTabletKSPs>
            <Stack
              direction="row"
              spacing={16}
              wrap="wrap"
              justify="start"
              align="center"
            >
              {includedExtrasItems.map(renderItem)}
            </Stack>
          </StyledTabletKSPs>
        )}

        {hasKeySellingPoints && (
          <StyledTabletKSPs>
            <Stack
              direction="column"
              spacing={16}
              wrap="wrap"
              justify="start"
              align="start"
            >
              {petsFreeItem && renderItem(petsFreeItem)}
              {keySellingPointItems.map(renderItem)}
            </Stack>
          </StyledTabletKSPs>
        )}
      </>
    )
  }

  return (
    <StyledMobileKSPs>
      <Stack
        direction="row"
        spacing={16}
        wrap="wrap"
        justify="start"
        align="center"
      >
        {hasIncludedExtras && includedExtrasItems.map(renderItem)}
        {hasKeySellingPoints && petsFreeItem && renderItem(petsFreeItem)}
        {hasKeySellingPoints && keySellingPointItems.map(renderItem)}
      </Stack>
    </StyledMobileKSPs>
  )
}
